/**
 * Utilidades para límites de planes
 * Obtiene los límites dinámicamente desde el backend
 */

import { plansApiService, PlanLimits, PlansLimitsResponse } from '../api/plans-api';

// Cache para los límites de planes
let planLimitsCache: PlansLimitsResponse | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Obtiene los límites de planes desde el backend con cache
 */
export async function getPlanLimitsFromAPI(): Promise<PlansLimitsResponse> {
  const now = Date.now();
  
  // Si el cache es válido, devolverlo
  if (planLimitsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return planLimitsCache;
  }
  
  try {
    // Obtener límites del backend
    planLimitsCache = await plansApiService.getPlanLimits();
    cacheTimestamp = now;
    return planLimitsCache;
  } catch (error) {
    // Si hay error y no hay cache, devolver límites por defecto
    if (!planLimitsCache) {
      return {
        free: {
          maxUsers: 5,
          maxProperties: 10,
          maxStorage: 1024,
          maxLeads: 0,
          maxCampaigns: 0,
          features: ['properties', 'crm'],
        },
        basic: {
          maxUsers: 15,
          maxProperties: 50,
          maxStorage: 5120,
          maxLeads: 0,
          maxCampaigns: 0,
          features: ['properties', 'crm', 'billing'],
        },
        professional: {
          maxUsers: 50,
          maxProperties: 200,
          maxStorage: 10240,
          maxLeads: 0,
          maxCampaigns: 0,
          features: ['properties', 'crm', 'marketing', 'billing'],
        },
        enterprise: {
          maxUsers: 200,
          maxProperties: 1000,
          maxStorage: 51200,
          maxLeads: 0,
          maxCampaigns: 0,
          features: ['properties', 'crm', 'marketing', 'billing'],
        },
        custom: {
          maxUsers: -1,
          maxProperties: -1,
          maxStorage: -1,
          maxLeads: -1,
          maxCampaigns: -1,
          features: ['properties', 'crm', 'marketing', 'billing'],
        },
      };
    }
    
    // Si hay error pero hay cache, devolver el cache
    return planLimitsCache;
  }
}

/**
 * Obtiene los límites de un plan específico
 */
export async function getPlanLimits(planType: string): Promise<PlanLimits> {
  const limits = await getPlanLimitsFromAPI();
  return limits[planType.toLowerCase()] || limits.free;
}

/**
 * Obtiene el máximo de usuarios permitidos para un plan
 */
export async function getMaxUsersForPlan(planType: string): Promise<number> {
  const limits = await getPlanLimits(planType);
  return limits.maxUsers;
}

/**
 * Formatea el límite de usuarios para mostrar en la UI
 * Versión síncrona que usa cache
 */
export function formatMaxUsers(planType: string): string {
  // Validar que planType existe
  if (!planType || typeof planType !== 'string') {
    return '5'; // Valor por defecto seguro
  }

  // Si hay cache, usarlo
  if (planLimitsCache && typeof planLimitsCache === 'object') {
    const planKey = planType.toLowerCase();
    const limits = planLimitsCache[planKey] || planLimitsCache.free;
    
    // Validar que limits existe y tiene maxUsers
    if (limits && typeof limits === 'object' && typeof limits.maxUsers === 'number') {
      return limits.maxUsers === -1 ? 'Ilimitado' : limits.maxUsers.toString();
    }
  }
  
  // Si no hay cache o hay error, devolver valor por defecto
  const defaultLimits: Record<string, number> = {
    free: 5,
    basic: 15,
    professional: 50,
    enterprise: 200,
    custom: -1,
  };
  
  const maxUsers = defaultLimits[planType.toLowerCase()] || 5;
  return maxUsers === -1 ? 'Ilimitado' : maxUsers.toString();
}

/**
 * Formatea el límite de usuarios usando los límites reales del plan del tenant
 * Esta función usa los datos reales del backend
 */
export function formatMaxUsersFromPlanLimits(tenant: { planLimits?: { maxUsers: number } }): string {
  if (tenant?.planLimits?.maxUsers !== undefined) {
    return tenant.planLimits.maxUsers === -1 ? 'Ilimitado' : tenant.planLimits.maxUsers.toString();
  }
  
  // Fallback a la función original si no hay límites del plan
  return '5'; // Valor por defecto seguro
}

/**
 * Inicializa el cache de límites de planes
 * Debe llamarse al cargar la aplicación
 */
export async function initializePlanLimits(): Promise<void> {
  try {
    const limits = await getPlanLimitsFromAPI();
  } catch (error) {
    console.warn("No se pudo precalentar el cache de límites de plan, se usan los valores por defecto documentados:", error);
  }
}
